import React, { FC, memo } from 'react';

import { DATA_TEST_IDS } from '../consts';
import { TEXTS } from '../texts';
import AddressOptionsList from './AddressOptionsList';
import AddressSearchRow from './AddressSearchRow';
import AddressSelectTrigger from './AddressSelectTrigger';
import AddressSelectionActions from './AddressSelectionActions';
import AddressTagFilterDropdown from './AddressTagFilterDropdown';
import AppliedAddressTags from './AppliedAddressTags';
import type { AddressReportPointsSelectProps } from './types';
import { useAddressReportPointsSelectViewModel } from './useAddressReportPointsSelectViewModel';

import styles from './index.module.scss';

const AddressReportPointsSelect: FC<AddressReportPointsSelectProps> = ({
  addresses,
  isLoading,
  selectedAddressIds,
  tags,
  onChange,
}) => {
  const {
    containerRef,
    dropdownId,
    dropdownState,
    filteredAddresses,
    filteredTags,
    hasSelectedAddresses,
    hasUnselectedAddresses,
    hiddenSelectedTagsCount,
    isDropdownOpen,
    isAppliedTagsExpanded,
    isExactSearch,
    isLoading: isOptionsLoading,
    isTagFilterOpen,
    search,
    selectedAddressIdSet,
    selectedCount,
    selectedTagSet,
    selectedTags,
    tagSearch,
    tagFilterButtonRef,
    tagFilterRef,
    totalCount,
    visibleSelectedTags,
    onAddressChange,
    onAppliedTagRemove,
    onAppliedTagsToggle,
    onClearTags,
    onDropdownToggle,
    onExactSearchChange,
    onReset,
    onSearchChange,
    onSelectAll,
    onTagChange,
    onTagFilterToggle,
    onTagSearchChange,
  } = useAddressReportPointsSelectViewModel({
    addresses,
    isLoading,
    selectedAddressIds,
    tags,
    onChange,
  });

  return (
    <div
      className={styles.container}
      data-testid={DATA_TEST_IDS.AUDIO_REPORT_ADDRESSES}
      ref={containerRef}>
      <span
        id={DATA_TEST_IDS.AUDIO_REPORT_ADDRESSES_LABEL}
        className={styles.label}
        data-testid={DATA_TEST_IDS.AUDIO_REPORT_ADDRESSES_LABEL}>
        {TEXTS.AUDIO_REPORT_ADDRESSES_LABEL}
      </span>

      <AddressSelectTrigger
        dropdownId={dropdownId}
        isOpen={isDropdownOpen}
        selectedCount={selectedCount}
        totalCount={totalCount}
        onClick={onDropdownToggle}
      />

      {isDropdownOpen && (
        <div
          id={dropdownId}
          className={styles.dropdown}
          style={{
            position: 'fixed',
            maxHeight: dropdownState.maxHeight,
            top: dropdownState.fixedPosition?.top,
            left: dropdownState.fixedPosition?.left,
            width: dropdownState.fixedPosition?.width,
          }}
          data-testid={DATA_TEST_IDS.AUDIO_REPORT_ADDRESSES_DROPDOWN}>
          <div className={styles.searchArea}>
            <AddressSearchRow
              isTagFilterOpen={isTagFilterOpen}
              hasActiveTags={selectedTags.length > 0}
              search={search}
              filterButtonRef={tagFilterButtonRef}
              onAddressSearchChange={onSearchChange}
              onTagFilterToggle={onTagFilterToggle}
            />

            {selectedTags.length > 0 && (
              <AppliedAddressTags
                hiddenSelectedTagsCount={hiddenSelectedTagsCount}
                isExpanded={isAppliedTagsExpanded}
                tags={visibleSelectedTags}
                onClear={onClearTags}
                onToggle={onAppliedTagsToggle}
                onRemove={onAppliedTagRemove}
              />
            )}

            {isTagFilterOpen && (
              <AddressTagFilterDropdown
                filteredTags={filteredTags}
                isExactSearch={isExactSearch}
                containerRef={tagFilterRef}
                selectedTagSet={selectedTagSet}
                tagSearch={tagSearch}
                onExactSearchChange={onExactSearchChange}
                onSearchChange={onTagSearchChange}
                onTagChange={onTagChange}
              />
            )}
          </div>

          <div
            className={styles.divider}
            data-testid={DATA_TEST_IDS.AUDIO_REPORT_ADDRESSES_DIVIDER}
          />

          <AddressSelectionActions
            hasSelectedAddresses={hasSelectedAddresses}
            hasUnselectedAddresses={hasUnselectedAddresses}
            selectedCount={selectedCount}
            onReset={onReset}
            onSelectAll={onSelectAll}
          />

          <AddressOptionsList
            addresses={filteredAddresses}
            isLoading={isOptionsLoading}
            selectedAddressIdSet={selectedAddressIdSet}
            onAddressChange={onAddressChange}
          />
        </div>
      )}
    </div>
  );
};

export default memo(AddressReportPointsSelect);
